import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { PagedResponse } from 'src/app/kuba/shared/models/pagedResponse';
import {
  AppraisalMeetingDto,
  AppraisalMeetingListItemDto,
  AppraisalMeetingCreateDto,
  AppraisalMeetingUpdateDto,
  AppraisalMeetingStatusUpdateDto,
  AppraisalMeetingPaginatedFilterDto,
  AppraisalMeetingResponseSubmitDto,
  AppraisalMeetingReassignManagerDto,
  AppraisalMeetingDashboardStatsDto
} from '../models';

@Injectable()
export class AppraisalMeetingService {
  private readonly API_BASE = `${environment.BASE_URL_V2}/appraisal/meetings`;

  constructor(private http: HttpClient) {}

  /**
   * Get paginated list of appraisal meetings for a business.
   * Non-admin callers are scoped to participant meetings plus completed history
   * for employees they currently manage.
   */
  getPagedList(
    businessId: number,
    filter: AppraisalMeetingPaginatedFilterDto
  ): Observable<PagedResponse<AppraisalMeetingListItemDto>> {
    const params = this.mapToHttpParams(filter);
    return this.http
      .get<PagedResponse<AppraisalMeetingListItemDto>>(
        `${this.API_BASE}/business/${businessId}/paged`,
        { params }
      )
      .pipe(map((result: any) => result), catchError(this.handleError));
  }

  /**
   * Get full meeting details including sections, questions, and responses.
   * Returns null if the caller has no participant or historical-manager access.
   */
  getById(id: number): Observable<AppraisalMeetingDto> {
    return this.http
      .get<AppraisalMeetingDto>(`${this.API_BASE}/${id}`)
      .pipe(map((result: any) => result), catchError(this.handleError));
  }

  /**
   * Create a new appraisal meeting.
   * Active relationships start immediately; other active business employees create a pending-access draft.
   */
  create(dto: AppraisalMeetingCreateDto): Observable<{ id: number }> {
    return this.http
      .post<{ id: number }>(this.API_BASE, dto)
      .pipe(map((result: any) => result), catchError(this.handleError));
  }

  /**
   * Update editable fields of a meeting (title, scheduled date, notes).
   * Only the meeting manager or Admin can call this.
   */
  update(id: number, dto: AppraisalMeetingUpdateDto): Observable<void> {
    return this.http
      .put<void>(`${this.API_BASE}/${id}`, dto)
      .pipe(map((result: any) => result), catchError(this.handleError));
  }

  /**
   * Update the status of a meeting.
   * Only the meeting manager or Admin can call this.
   */
  updateStatus(id: number, dto: AppraisalMeetingStatusUpdateDto): Observable<void> {
    return this.http
      .patch<void>(`${this.API_BASE}/${id}/status`, dto)
      .pipe(map((result: any) => result), catchError(this.handleError));
  }

  /**
   * Soft-delete a meeting.
   * Only the meeting manager or Admin can call this.
   */
  delete(id: number): Observable<void> {
    return this.http
      .delete<void>(`${this.API_BASE}/${id}`)
      .pipe(map((result: any) => result), catchError(this.handleError));
  }

  /**
   * Permanently delete an archived meeting.
   * Only the archived meeting manager participant or Admin can call this.
   */
  deleteArchived(id: number): Observable<void> {
    return this.http
      .delete<void>(`${this.API_BASE}/${id}/archived`)
      .pipe(map((result: any) => result), catchError(this.handleError));
  }

  restore(id: number): Observable<void> {
    return this.http
      .patch<void>(`${this.API_BASE}/${id}/restore`, {})
      .pipe(map((result: any) => result), catchError(this.handleError));
  }

  /**
   * Submit or update responses for a meeting.
   * Only the manager or employee of the meeting can call this.
   */
  submitResponses(
    meetingId: number,
    responses: AppraisalMeetingResponseSubmitDto[]
  ): Observable<void> {
    return this.http
      .post<void>(`${this.API_BASE}/${meetingId}/responses`, responses)
      .pipe(map((result: any) => result), catchError(this.handleError));
  }

  saveResponses(
    meetingId: number,
    responses: AppraisalMeetingResponseSubmitDto[]
  ): Observable<void> {
    return this.http
      .post<void>(`${this.API_BASE}/${meetingId}/responses/save`, responses)
      .pipe(map((result: any) => result), catchError(this.handleError));
  }

  /**
   * Get all submitted responses for a meeting.
   */
  getResponses(meetingId: number): Observable<AppraisalMeetingResponseSubmitDto[]> {
    return this.http
      .get<AppraisalMeetingResponseSubmitDto[]>(`${this.API_BASE}/${meetingId}/responses`)
      .pipe(map((result: any) => result), catchError(this.handleError));
  }

  downloadReport(meetingId: number): Observable<HttpResponse<Blob>> {
    if (!meetingId || meetingId <= 0) {
      return throwError(() => new Error('Invalid dialog meeting ID'));
    }

    return this.http
      .get(`${this.API_BASE}/${meetingId}/report`, {
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(catchError(this.handleError));
  }

  /**
   * Record the current caller's signature on the meeting.
   * Either the manager or employee can sign.
   */
  sign(meetingId: number): Observable<void> {
    return this.http
      .post<void>(`${this.API_BASE}/${meetingId}/sign`, {})
      .pipe(map((result: any) => result), catchError(this.handleError));
  }

  /**
   * Resend the appraisal invitation email to the employee.
   * Only the meeting manager or Admin can call this.
   */
  resendEmail(meetingId: number): Observable<void> {
    return this.http
      .post<void>(`${this.API_BASE}/${meetingId}/resend-email`, {})
      .pipe(map((result: any) => result), catchError(this.handleError));
  }

  getDashboardStats(
    businessId: number,
    viewScope: 'manager' | 'employee'
  ): Observable<AppraisalMeetingDashboardStatsDto> {
    return this.http
      .get<AppraisalMeetingDashboardStatsDto>(
        `${this.API_BASE}/business/${businessId}/dashboard-stats`,
        { params: { viewScope } }
      )
      .pipe(map((result: any) => result), catchError(this.handleError));
  }

  /**
   * Reassign the manager of a meeting to a different user.
   * Admin only. The new manager must have an active relationship with the employee.
   */
  reassignManager(
    meetingId: number,
    dto: AppraisalMeetingReassignManagerDto
  ): Observable<void> {
    return this.http
      .patch<void>(`${this.API_BASE}/${meetingId}/reassign-manager`, dto)
      .pipe(map((result: any) => result), catchError(this.handleError));
  }

  private mapToHttpParams<T extends Record<string, any>>(dto: T): HttpParams {
    let params = new HttpParams();
    Object.entries(dto).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params = params.set(key, String(value));
      }
    });
    return params;
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Client Error: ${error.error.message}`;
    } else {
      switch (error.status) {
        case 0:
          errorMessage = 'Unable to connect to the server. Please check your connection.';
          break;
        case 400:
          errorMessage = error.error?.message || 'Invalid request data';
          break;
        case 401:
          errorMessage = 'Unauthorized. Please log in again.';
          break;
        case 403:
          errorMessage = 'You do not have permission to perform this action.';
          break;
        case 404:
          errorMessage = 'The requested resource was not found.';
          break;
        default:
          errorMessage = error.status >= 500
            ? 'A server error occurred. Please try again later.'
            : error.error?.message || `Server Error: ${error.status}`;
      }
    }
    console.error('AppraisalMeetingService Error:', errorMessage, error);
    return throwError(() => new Error(errorMessage));
  }
}
